import {
  DesignatedTruckCarModelOptions,
  DesignatedTruckFeatureOptions,
  DesignatedTruckFloorSpecificationOptions,
  DesignatedTruckKlassOptions,
  DesignatedTruckLoadingPlatformHeightOptions,
  DesignatedTruckLoadingPlatformLengthOptions,
  DesignatedTruckLoadingPlatformWidthOptions,
} from 'src/entities/orderEntity';

import { FilterColumnDef } from './types';

export type BuildFilterColumnsParams = {
  /** license.custom_input_fields（会社ごとの任意項目名） */
  customInputFieldNames: string[];
  trucks: { id: number; name: string }[];
  drivers: { id: number; name: string }[];
};

/** 任意項目を除いた固定列数。 */
export const FIXED_COLUMN_COUNT = 27;

const enumOptions = (values: readonly string[]) => values.map((v) => ({ value: v, label: v }));

export function buildFilterColumns({ customInputFieldNames, trucks, drivers }: BuildFilterColumnsParams): FilterColumnDef[] {
  const fixed: FilterColumnDef[] = [
    { key: 'code', label: '伝票No', type: 'text', getValue: (lr) => lr.code },
    { key: 'shipperName', label: '荷主名', type: 'text', getValue: (lr) => lr.shipper_name },
    { key: 'loadingName', label: '積地名称', type: 'text', getValue: (lr) => lr.loading_name },
    { key: 'loadingAddress', label: '積地住所', type: 'text', getValue: (lr) => lr.loading_address },
    { key: 'loadingStartAt', label: '積地指定開始時間', type: 'time', getValue: (lr) => lr.loading_start_at },
    { key: 'loadingEndAt', label: '積地指定終了時間', type: 'time', getValue: (lr) => lr.loading_end_at },
    { key: 'unloadingName', label: '降地名称', type: 'text', getValue: (lr) => lr.unloading_name },
    { key: 'unloadingAddress', label: '降地住所', type: 'text', getValue: (lr) => lr.unloading_address },
    { key: 'unloadingStartAt', label: '降地指定開始時間', type: 'time', getValue: (lr) => lr.unloading_start_at },
    { key: 'unloadingEndAt', label: '降地指定終了時間', type: 'time', getValue: (lr) => lr.unloading_end_at },
    { key: 'itemNames', label: '品名', type: 'text', getValue: (lr) => (lr.items ?? []).map((i) => i.name) },
    { key: 'cases', label: '数量', type: 'number', getValue: (lr) => lr.item_count },
    { key: 'weight', label: '重量', type: 'number', getValue: (lr) => lr.item_total_weight_kg },
    { key: 'volume', label: '体積', type: 'number', getValue: (lr) => lr.item_total_volume_m3 },
    { key: 'handlingOfCargoStyle', label: '荷扱', type: 'text', getValue: (lr) => lr.item_handling_of_cargo_style },
    { key: 'itemPackingStyles', label: '荷姿', type: 'text', getValue: (lr) => (lr.items ?? []).map((i) => i.item_packing_style) },
    { key: 'canBeMixed', label: '積合せ可否', type: 'boolean', getValue: (lr) => lr.item_can_be_mixed },
    { key: 'truckKlasses', label: '指定車両タイプ', type: 'enumSelect', options: enumOptions(DesignatedTruckKlassOptions), getValue: (lr) => lr.designated_truck_klasses },
    { key: 'carModels', label: '指定車種', type: 'enumSelect', options: enumOptions(DesignatedTruckCarModelOptions), getValue: (lr) => lr.designated_truck_car_models },
    { key: 'platformHeights', label: '荷台高さ', type: 'enumSelect', options: enumOptions(DesignatedTruckLoadingPlatformHeightOptions), getValue: (lr) => lr.designated_truck_loading_platform_heights },
    { key: 'platformWidths', label: '荷台幅', type: 'enumSelect', options: enumOptions(DesignatedTruckLoadingPlatformWidthOptions), getValue: (lr) => lr.designated_truck_loading_platform_widths },
    { key: 'platformLengths', label: '荷台長さ', type: 'enumSelect', options: enumOptions(DesignatedTruckLoadingPlatformLengthOptions), getValue: (lr) => lr.designated_truck_loading_platform_lengths },
    { key: 'floorSpecifications', label: '床仕様', type: 'enumSelect', options: enumOptions(DesignatedTruckFloorSpecificationOptions), getValue: (lr) => lr.designated_truck_floor_specifications },
    { key: 'truckFeatures', label: '装置', type: 'enumSelect', options: enumOptions(DesignatedTruckFeatureOptions), getValue: (lr) => lr.designated_truck_features },
    // 名前で比較すると同名で誤一致し、名称変更で保存済みプリセットが無言で効かなくなるため ID で比較する。
    { key: 'allowedTruckIds', label: '指定トラック', type: 'idSelect', options: trucks.map((t) => ({ value: t.id, label: t.name })), getValue: (lr) => lr.allowed_truck_ids },
    { key: 'deniedDriverIds', label: 'NGドライバー', type: 'idSelect', options: drivers.map((d) => ({ value: d.id, label: d.name })), getValue: (lr) => lr.denied_driver_ids },
    { key: 'memo', label: '備考', type: 'text', getValue: (lr) => lr.memo },
  ];

  const custom: FilterColumnDef[] = customInputFieldNames.map((name) => ({
    key: `custom:${name}`,
    label: name,
    type: 'text',
    getValue: (lr) => (lr.custom_input_fields ?? []).find((f) => f.key === name)?.value,
  }));

  return [...fixed, ...custom];
}
